import { Component, ElementRef, Injector, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import {
  BaseResourceFormComponent, ContaBancaria, DateFormatPipe, EddyAutoComplete, 
  GlobalService, LoginContabil, Orgao, OrgaoService, TransferenciaBancaria
} from 'eddydata-lib';
import { MessageService } from 'primeng/api';
import { takeUntil } from 'rxjs/operators';
import { TransferenciaBancariaService, ContaBancariaService } from 'administrativo-lib';
import { ContaBancariaSaldoService } from '../../conta-bancaria/service/conta-bancaria-saldo.service';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-transferencia-bancaria-form',
  templateUrl: './transferencia-bancaria-form.component.html'
})
export class TransferenciaBancariaFormComponent extends BaseResourceFormComponent<TransferenciaBancaria, LoginContabil> {

  /**
   * Declaração de variáveis
   */
  @ViewChild('especie_') inputField: ElementRef;

  public orgaoPagadora: Orgao;
  public orgaoRecebedora: Orgao;
  public pagadoraAutoComplete: EddyAutoComplete<ContaBancaria>;
  public recebedoraAutoComplete: EddyAutoComplete<ContaBancaria>;
  public listaEspecies: Array<any>;
  public listaOrgaos: Orgao[];
  public saldoPagadora = 0;
  public saldoRecebedora = 0;

  /**
   * Construtor com as injeções de dependencias
   */
  constructor(
    private messageService: MessageService,
    protected injector: Injector,
    protected globalService: GlobalService,
    protected contaService: ContaBancariaService,
    protected contaSaldoService: ContaBancariaSaldoService,
    protected orgaoService: OrgaoService,
    protected transferenciaService: TransferenciaBancariaService) {
    super(new TransferenciaBancaria(), injector, TransferenciaBancaria.converteJson, transferenciaService);
  }

  // ========================================================================
  //                        MÉTODOS ABSTRAÍDOS
  // ========================================================================


  protected criarCamposForm(): void {
    this.entidadeForm = this.fb.group({
      id: [null],
      data_transfere: [null, [Validators.required]],
      data_referencia: [null],
      valor_transferido: 0,
      mes: [null],
      aux: [0],
      especie: ['N', [Validators.required]],
      documento: ['TRANSFERÊNCIA', [Validators.required]],
      historico: ['TRANSFERÊNCIA BANCÁRIA', [Validators.required, Validators.minLength(2)]],
      tipo: [1, [Validators.required]],
      exercicio: [this.login.exercicio, [Validators.required]],
      orgao: [this.login.orgao, [Validators.required]],
      conta_pagadora: [null, [Validators.required]],
      conta_recebedora: [null, [Validators.required]]
    });
  }


  protected parametrosExtras(): {} {
    return {
      relations: 'conta_pagadora,conta_pagadora.banco,' +
        'conta_pagadora.orgao,conta_recebedora,conta_recebedora.orgao,conta_recebedora.banco'
    };
  }

  protected afterLoad() {
    this.entidade.data_transfere = new DateFormatPipe().transform(this.entidade.data_transfere, []);
    this.entidade.data_referencia = new DateFormatPipe().transform(this.entidade.data_referencia, []);

    this.recebedoraAutoComplete.id = this.entidade.conta_recebedora ? this.entidade.conta_recebedora.id : null;
    this.pagadoraAutoComplete.id = this.entidade.conta_pagadora ? this.entidade.conta_pagadora.id : null;

    this.orgaoPagadora = this.entidade.conta_pagadora.orgao;
    this.orgaoRecebedora = this.entidade.conta_recebedora.orgao;

    this.buscarSaldoConta('P');
    this.buscarSaldoConta('R');
  }

  protected afterInit(): void {
    this.orgaoPagadora = this.login.orgao;
    this.orgaoRecebedora = this.login.orgao;
    this.carregarPagadora();
    this.carregarRecebedora();
    this.orgaoService.obterTodos()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(dados => {
        this.listaOrgaos = dados;
      });
    this.listaEspecies = [
      { id: 'N', nome: 'NORMAL' },
      { id: 'D', nome: 'DUODÉCIMO' },
      { id: 'DD', nome: 'DEVOLUÇÃO DE DUODÉCIMO' },
      { id: 'E', nome: 'EDUCAÇÃO' },
      { id: 'S', nome: 'SAÚDE' },
      { id: 'A', nome: 'ASSISTÊNCIA SOCIAL' }
    ];
    if (this.currentActionRoute === 'novo') {
      this.transferenciaService.ultimaDataTransferencia(this.login.exercicio.id, this.login.orgao.id)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((res) => {
          if (res === 0) {
            this.entidade.data_transfere = new Date();
          } else {
            this.entidade.data_transfere = new DateFormatPipe().transform(res, []);
          }
          this.entidadeForm.get('data_transfere').setValue(this.entidade.data_transfere);
        });
    }
  }

  protected campoFoco(): ElementRef {
    return this.inputField;
  }

  protected beforeSubmit() {
    try {
      const dataTransfere = new DateFormatPipe().transform(this.entidadeForm.get('data_transfere').value, []);
      this.entidadeForm.get('mes').setValue(dataTransfere.getUTCMonth() + 1);
      this.entidadeForm.get('aux').setValue(0);
      if (this.entidadeForm.get('conta_pagadora').value === this.entidadeForm.get('conta_recebedora').value) {
        throw new Error('A conta recebedora e pagadora são as mesma, selecione outra conta!');
      }

    } catch (e) {
      this.messageService.add({ severity: 'error', summary: 'Validação', detail: e });
      throw e;
    }
  }

  protected afterSubmit(ent: TransferenciaBancaria): void {
    if (this.currentActionRoute === 'novo') {
      this.router.navigate(['/transferencias-bancarias', ent.id, 'editar']);
    }
  }

  // ========================================================================
  //                            MÉTODOS DA CLASSE
  // ========================================================================


  public carregarPagadora() {
    this.entidadeForm.get('conta_pagadora').setValue(null);
    this.pagadoraAutoComplete = new EddyAutoComplete(this.entidadeForm.get('conta_pagadora'), this.contaService,
      'id', ['numero_conta', 'banco.nome', 'nome'], { orgao_id: this.orgaoPagadora.id, orderBy: 'nome', relations: 'orgao,banco' },
      { number: ['numero_conta'], text: ['nome'] },
      () => {
        this.buscarSaldoConta('P');
      }
    );
  }

  public carregarRecebedora() {
    this.entidadeForm.get('conta_recebedora').setValue(null);
    this.recebedoraAutoComplete = new EddyAutoComplete(this.entidadeForm.get('conta_recebedora'), this.contaService,
      'id', ['numero_conta', 'banco.nome', 'nome'], { orgao_id: this.orgaoRecebedora.id, orderBy: 'nome', relations: 'orgao,banco' },
      { number: ['numero_conta'], text: ['nome'] },
      () => {
        this.buscarSaldoConta('R');
      }
    );
  }

  private buscarSaldoConta(tipo: 'P' | 'R') {
    const contaId = Number(tipo === 'P' ? this.pagadoraAutoComplete.id : this.recebedoraAutoComplete.id);
    const orgaoId = Number(tipo === 'P' ? this.orgaoPagadora.id : this.orgaoRecebedora.id);
    if (contaId) {
      let datepipe: DatePipe = new DatePipe('pt');
      const dataTransfere = this.entidadeForm.get('data_transfere').value ? datepipe.transform(this.entidadeForm.get('data_transfere').value, 'yyyy-MM-dd', 'GMT') :
        datepipe.transform(this.entidade.data_transfere, 'yyyy-MM-dd', 'GMT');
      this.contaService.obterSaldoBancarioConta(contaId, this.login.exercicio.id, orgaoId, dataTransfere)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((res) => {
          if (tipo === 'P') {
            this.saldoPagadora = res;
          } else {
            this.saldoRecebedora = res;
          }
        });
    }
  }

  public voltar() {
    if (this.login.sistema === 'contabil') {
      this.router.navigate(['/tesouraria']);
    } else {
      this.sair();
    }
  }
}
